import React, { useState, useEffect, useRef } from 'react';
import { DashboardView, dashboardViewsAPIsService } from '../hooks/useDashboardViews';
import { toast } from 'react-toastify';
import AppLoading from './AppLoading';
import { GOOGLE_CHARTS_TYPES } from '../consts/graphs';
import '../styles/PageEditorView.scss';

interface GraphCard {
  id: number;
  chartId: string;
  title: string;
  chartType: string;
  order: number;
  boxWidth: string;
  boxHeight?: string;
  isEnabled: number;
  hideColumnsByIndex?: string;
  additionalConfig?: string;
  selectedVersion?: number;
}

interface FilterItem {
  id: number;
  name: string;
  type: string;
  order: number;
  isActive: number;
}

// Available filters from seed file
const AVAILABLE_FILTERS = [
  { id: 1, name: "Organisations" },
  { id: 2, name: "Site" },
  { id: 3, name: "Role" },
  { id: 4, name: "Users" },
  { id: 5, name: "Courses" },
  { id: 6, name: "Ratings" },
  { id: 7, name: "Authors" },
  { id: 8, name: "Credits" },
  { id: 9, name: "invertData" },
  { id: 10, name: "Team" },
  { id: 11, name: "datepicker" },
  { id: 12, name: "rangepicker" },
  { id: 13, name: "eLearning/Events" },
];

interface PageEditorViewProps {
  dashboard: DashboardView;
  onBack: () => void;
  onSave: () => void;
}

const PageEditorView: React.FC<PageEditorViewProps> = ({ dashboard, onBack, onSave }) => {
  const [graphs, setGraphs] = useState<GraphCard[]>([]);
  const [filters, setFilters] = useState<FilterItem[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const [draggedGraph, setDraggedGraph] = useState<{ id: number; index: number } | null>(null);
  const [draggedFilter, setDraggedFilter] = useState<{ id: number; index: number } | null>(null);
  const [dragOverGraphIndex, setDragOverGraphIndex] = useState<number | null>(null);
  const [dragOverFilterIndex, setDragOverFilterIndex] = useState<number | null>(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // Initialize data
  useEffect(() => {
    if (dashboard.graphs) {
      setGraphs([...dashboard.graphs]);
    }
    if (dashboard.filters) {
      setFilters([...dashboard.filters]);
    }
  }, [dashboard]);

  // Track changes
  useEffect(() => {
    const originalGraphs = dashboard.graphs || [];
    const originalFilters = dashboard.filters || [];
    
    const graphsChanged = JSON.stringify(graphs) !== JSON.stringify(originalGraphs);
    const filtersChanged = JSON.stringify(filters) !== JSON.stringify(originalFilters);
    
    setHasChanges(graphsChanged || filtersChanged);
  }, [graphs, filters, dashboard]);

  const handleGraphDragStart = (e: React.DragEvent, id: number, index: number) => {
    setDraggedGraph({ id, index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  };

  const handleGraphDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverGraphIndex(index);
  };

  const handleGraphDragLeave = () => {
    setDragOverGraphIndex(null);
  };

  const handleGraphDragEnd = () => {
    setDraggedGraph(null);
    setDragOverGraphIndex(null);
  };

  const handleGraphDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (!draggedGraph || draggedGraph.index === dropIndex) {
      setDraggedGraph(null);
      setDragOverGraphIndex(null);
      return;
    }

    const newGraphs = [...graphs];

    // Swap the dragged graph with the drop target
    [newGraphs[draggedGraph.index], newGraphs[dropIndex]] = [
      newGraphs[dropIndex],
      newGraphs[draggedGraph.index]
    ];
    
    // Update order
    const updatedGraphs = newGraphs.map((graph, index) => ({
      ...graph,
      order: index + 1
    }));
    
    setGraphs(updatedGraphs);
    setDraggedGraph(null);
    setDragOverGraphIndex(null);
  };

  const handleFilterDragStart = (e: React.DragEvent, id: number, index: number) => {
    setDraggedFilter({ id, index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  };

  const handleFilterDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFilterIndex(index);
  };

  const handleFilterDragLeave = () => {
    setDragOverFilterIndex(null);
  };

  const handleFilterDragEnd = () => {
    setDraggedFilter(null);
    setDragOverFilterIndex(null);
  };

  const handleFilterDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (!draggedFilter || draggedFilter.index === dropIndex) {
      setDraggedFilter(null);
      setDragOverFilterIndex(null);
      return;
    }

    const newFilters = [...filters];

    // Swap the dragged filter with the drop target
    [newFilters[draggedFilter.index], newFilters[dropIndex]] = [
      newFilters[dropIndex],
      newFilters[draggedFilter.index]
    ];
    
    // Update order
    const updatedFilters = newFilters.map((filter, index) => ({
      ...filter,
      order: index + 1
    }));
    
    setFilters(updatedFilters);
    setDraggedFilter(null);
    setDragOverFilterIndex(null);
  };

  const updateGraph = (graphId: number, updates: Partial<GraphCard>) => {
    setGraphs(prev => prev.map(graph => 
      graph.id === graphId ? { ...graph, ...updates } : graph
    ));
  };

  const toggleGraphEnabled = (graphId: number) => {
    updateGraph(graphId, { isEnabled: graphs.find(g => g.id === graphId)?.isEnabled === 1 ? 0 : 1 });
  };

  const removeFilter = (filterId: number) => {
    setFilters(prev => prev.filter(filter => filter.id !== filterId));
  };

  const addFilter = (filterId: number) => {
    const filter = AVAILABLE_FILTERS.find(f => f.id === filterId);
    if (filter && !filters.find(f => f.id === filterId)) {
      const newFilter: FilterItem = {
        id: filter.id,
        name: filter.name,
        type: 'filter',
        order: filters.length + 1,
        isActive: 1
      };
      setFilters(prev => [...prev, newFilter]);
      setShowFilterDropdown(false);
    }
  };

  const toggleFilterDropdown = () => {
    setShowFilterDropdown(!showFilterDropdown);
  };

  const getAvailableFilters = () => {
    return AVAILABLE_FILTERS.filter(filter => !filters.find(f => f.id === filter.id));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      // Prepare graphs data for API
      const graphsData = graphs.map(graph => ({
        id: graph.id,
        title: graph.title,
        chartType: graph.chartType,
        order: graph.order,
        boxWidth: graph.boxWidth,
        boxHeight: graph.boxHeight,
        isEnabled: graph.isEnabled,
        hideColumnsByIndex: graph.hideColumnsByIndex,
        additionalConfig: graph.additionalConfig
      }));

      // Prepare filters data for API
      const filtersData = filters.map(filter => ({
        id: filter.id,
        order: filter.order
      }));

      // Call API to update graphs and filters
      await dashboardViewsAPIsService.updatePageGraphs(dashboard.id, graphsData, filtersData);
      
      toast.success('Page saved successfully!');
      onSave();
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error('Failed to save page');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (hasChanges) {
      setShowBackConfirm(true);
    } else {
      onBack();
    }
  };

  const handleBackConfirm = () => {
    setShowBackConfirm(false);
    onBack();
  };

  const handleReset = async () => {
    try {
      setIsSaving(true);
      
      // Call API to reset dashboard data to default
      const result = await dashboardViewsAPIsService.resetDashboardData(dashboard.id);
      
      // Update local state with reset data
      if (result.success && result.data) {
        setGraphs(result.data.graphs || []);
        setFilters(result.data.filters || []);
        toast.success('Page reset to original state');
      } else {
        toast.error('Failed to reset page');
      }
    } catch (error) {
      console.error('Error resetting page:', error);
      toast.error('Failed to reset page');
    } finally {
      setIsSaving(false);
      onBack();
    }
  };

  return (
    <div className="page-editor-view">
      <div className="editor-header">
        <div className="header-content">
          <button className="back-btn" onClick={handleBack}>
            Back
          </button>
          <h2>Edit Page: {dashboard.name}</h2>
          <p>{dashboard.description}</p>
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-secondary reset-btn"
            onClick={handleReset}
            disabled={isSaving}
          >
            {isSaving ? 'Resetting...' : 'Reset Page'}
          </button>
          <button 
            className="btn btn-primary save-btn"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="editor-content">
        {/* Filters Section */}
        <div className="filters-section">
          <h3>Page Filters</h3>
          <div className="filters-tray">
            {filters.map((filter, index) => (
            <div
                key={filter.id}
                className={`filter-box ${draggedFilter?.id === filter.id ? 'dragging' : ''} ${dragOverFilterIndex === index && draggedFilter?.id !== filter.id ? 'drag-over' : ''}`}
                draggable
                onDragStart={(e) => handleFilterDragStart(e, filter.id, index)}
              onDragEnd={handleFilterDragEnd}
                onDragOver={(e) => handleFilterDragOver(e, index)}
                onDragLeave={handleFilterDragLeave}
                onDrop={(e) => handleFilterDrop(e, index)}
              >
                <button 
                  className="remove-filter-btn"
                  onClick={() => removeFilter(filter.id)}
                  title="Remove filter"
                >
                  ×
                </button>
                <div className="filter-content">
                  <span className="filter-name">{filter.name}</span>
                  <span className="filter-type">{filter.type}</span>
                </div>
                <div className="filter-drag-handle">⋮⋮</div>
              </div>
            ))}
            <div className="add-filter-container">
              <button 
                className="add-filter-btn" 
                onClick={toggleFilterDropdown} 
                title="Add filter"
              >
                +
              </button>
              {showFilterDropdown && (
                <div className="filter-dropdown">
                  {getAvailableFilters().length > 0 ? (
                    getAvailableFilters().map(filter => (
                      <button
                        key={filter.id}
                        className="filter-option"
                        onClick={() => addFilter(filter.id)}
                      >
                        {filter.name}
                      </button>
                    ))
                  ) : (
                    <div className="no-filters">No more filters available</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Graphs Section */}
        <div className="graphs-section">
          <h3>Page Graphs</h3>
          <div className="graphs-grid">
            {graphs.map((graph, index) => (
                <div
                key={graph.id}
                className={`graph-card ${draggedGraph?.id === graph.id ? 'dragging' : ''} ${dragOverGraphIndex === index && draggedGraph?.id !== graph.id ? 'drag-over' : ''} ${graph.isEnabled === 0 ? 'disabled' : ''}`}
                draggable
                onDragStart={(e) => handleGraphDragStart(e, graph.id, index)}
                  onDragEnd={handleGraphDragEnd}
                onDragOver={(e) => handleGraphDragOver(e, index)}
                onDragLeave={handleGraphDragLeave}
                onDrop={(e) => handleGraphDrop(e, index)}
              >
                <div className="graph-header">
                  <input
                    type="text"
                    value={graph.title}
                    onChange={(e) => updateGraph(graph.id, { title: e.target.value })}
                    className="graph-title-input"
                  />
                </div>

                <div className="graph-settings">
                  <div className="setting-group">
                    <label>Chart Type:</label>
                    <select
                      value={graph.chartType}
                      onChange={(e) => updateGraph(graph.id, { chartType: e.target.value })}
                      className="chart-type-select"
                    >
                      {Object.values(GOOGLE_CHARTS_TYPES).map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div className="setting-group">
                    <label>Width:</label>
                    <div className="dimension-input-wrapper">
                      <input
                        type="number"
                        value={graph.boxWidth ? parseInt(graph.boxWidth) || '' : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          updateGraph(graph.id, { boxWidth: value ? `${value}px` : '' });
                        }}
                        className="dimension-input"
                        placeholder="e.g., 300"
                        min="1"
                      />
                      <span className="dimension-unit">px</span>
                    </div>
                  </div>

                  <div className="setting-group">
                    <label>Height:</label>
                    <div className="dimension-input-wrapper">
                      <input
                        type="number"
                        value={graph.boxHeight ? parseInt(graph.boxHeight) || '' : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          updateGraph(graph.id, { boxHeight: value ? `${value}px` : '' });
                        }}
                        className="dimension-input"
                        placeholder="e.g., 200"
                        min="1"
                      />
                      <span className="dimension-unit">px</span>
                    </div>
                  </div>
                </div>

                <div className="graph-drag-handle">
                  <div className="drag-icon">
                    <div className="drag-symbol">⋮⋮</div>
                    <div className="drag-overlay">Drag to reorder</div>
                  </div>
                  <div className="graph-controls">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={graph.isEnabled === 1}
                        onChange={() => toggleGraphEnabled(graph.id)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Back Confirmation Modal */}
      {showBackConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Unsaved Changes</h3>
            <p>You have unsaved changes. Are you sure you want to go back without saving?</p>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowBackConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleBackConfirm}
              >
                Go Back Without Saving
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageEditorView;
